import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CATEGORY_CONFIG, FORM_CATEGORIES, toLocalInput } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import DateTimePicker from "@/components/DateTimePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Loader2, Check, Search, Users, UserPlus, X, FileText, Package, Plus, Upload } from "lucide-react";

function Section({ title, children }) {
  return (
    <div className="stark-card p-6">
      <h2 className="mb-5 text-sm font-bold uppercase tracking-wide text-stark-muted">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function genPassword() {
  return "Stark" + Math.random().toString(36).slice(2, 8) + "!";
}

export default function JobFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [f, setF] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    contact_name: "", contact_role: "", contact_phone: "",
    category: "installation",
    job_description: "", po_number: "",
    address: "", dest_lat: null, dest_lng: null, gated_property: false,
    scheduled_at: "", window_minutes: 60,
    multi_day: false, end_at: "",
    truck_id: "", assigned_tech_ids: [], am_name: "", am_email: "", notes_for_techs: "",
    duration_min: "", items: [],
  });
  const [trucks, setTrucks] = useState([]);
  const [techs, setTechs] = useState([]);
  const [ams, setAms] = useState([]);
  const [geoResults, setGeoResults] = useState([]);
  const [geoBusy, setGeoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!editing);
  const [files, setFiles] = useState([]); // {file} pending upload (create mode)
  const [crewOpen, setCrewOpen] = useState(false);
  const [crew, setCrew] = useState({ name: "", email: "" });
  const [crewBusy, setCrewBusy] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadState, setUploadState] = useState({}); // fileId -> { progress, status }
  const geoTimer = useRef(null);
  const fileRef = useRef(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const team = CATEGORY_CONFIG[f.category]?.team || "installation";

  const loadTechs = () => api.get("/users", { params: { role: "driver", team } }).then(({ data }) => setTechs(data)).catch(() => {});

  useEffect(() => {
    api.get("/trucks", { params: { category: team } }).then(({ data }) => setTrucks(data)).catch(() => {});
    loadTechs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  useEffect(() => {
    api.get("/users", { params: { role: "am" } }).then(({ data }) => setAms(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editing) return;
    api.get(`/deliveries/${id}`).then(({ data }) => {
      setF({
        customer_name: data.customer_name || "", customer_email: data.customer_email || "",
        customer_phone: data.customer_phone || "",
        contact_name: data.contact?.name || "", contact_role: data.contact?.role || "", contact_phone: data.contact?.phone || "",
        category: FORM_CATEGORIES.includes(data.category) ? data.category : "installation",
        job_description: data.job_description || "", po_number: data.po_number || "",
        address: data.address || "", dest_lat: data.dest_lat, dest_lng: data.dest_lng,
        gated_property: data.gated_property || false,
        scheduled_at: toLocalInput(data.scheduled_at), window_minutes: data.window_minutes || 60,
        multi_day: data.multi_day || false, end_at: toLocalInput(data.end_at),
        truck_id: data.truck_id || "", assigned_tech_ids: data.assigned_tech_ids || [],
        am_name: data.am_name || "", am_email: data.am_email || "", notes_for_techs: data.notes_for_techs || "",
        duration_min: data.estimated_duration_min != null ? String(data.estimated_duration_min) : "",
        items: (data.items || []).map((it) => ({ name: it.name || "", quantity: it.quantity || "", note: it.note || "" })),
      });
      setLoaded(true);
    }).catch(() => { toast.error("Job not found"); navigate("/jobs"); });
  }, [editing, id, navigate]);

  const searchAddress = (q) => {
    set("address", q); set("dest_lat", null); set("dest_lng", null);
    if (geoTimer.current) clearTimeout(geoTimer.current);
    if (q.trim().length < 3) { setGeoResults([]); return; }
    geoTimer.current = setTimeout(async () => {
      setGeoBusy(true);
      try { const { data } = await api.get("/geocode", { params: { q } }); setGeoResults(data); }
      catch { setGeoResults([]); } finally { setGeoBusy(false); }
    }, 500);
  };
  const pickAddress = (hit) => { set("address", hit.label); set("dest_lat", hit.lat); set("dest_lng", hit.lng); setGeoResults([]); };

  const toggleTech = (tid) => setF((p) => ({
    ...p,
    assigned_tech_ids: p.assigned_tech_ids.includes(tid) ? p.assigned_tech_ids.filter((x) => x !== tid) : [...p.assigned_tech_ids, tid],
  }));

  const addItem = () => setF((p) => ({ ...p, items: [...p.items, { name: "", quantity: "", note: "" }] }));
  const setItem = (i, k, v) => setF((p) => ({ ...p, items: p.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const removeItem = (i) => setF((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const onPickAM = (name) => {
    set("am_name", name);
    const match = ams.find((a) => a.name === name);
    if (match) set("am_email", match.email);
  };

  const addCrew = async () => {
    if (!crew.name || !crew.email) return toast.error("Name & email required");
    setCrewBusy(true);
    const pw = genPassword();
    try {
      const { data } = await api.post("/users", { email: crew.email, name: crew.name, role: "driver", team, password: pw });
      toast.success(`${data.name} added · temp password: ${pw}`, { duration: 8000 });
      setCrew({ name: "", email: "" });
      setCrewOpen(false);
      await loadTechs();
      setF((p) => ({ ...p, assigned_tech_ids: [...p.assigned_tech_ids, data.id] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add crew");
    } finally { setCrewBusy(false); }
  };

  const addFiles = (list) => {
    const valid = Array.from(list || []).filter((file) => {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5 MB`); return false; }
      return true;
    });
    if (!valid.length) return;
    const items = valid.map((file) => ({
      id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())),
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setFiles((p) => [...p, ...items]);
  };
  const onFiles = (e) => { addFiles(e.target.files); if (e.target) e.target.value = ""; };
  const removeFile = (idx) => setFiles((p) => {
    const it = p[idx];
    if (it?.preview) URL.revokeObjectURL(it.preview);
    if (it) setUploadState((s) => { const n = { ...s }; delete n[it.id]; return n; });
    return p.filter((_, i) => i !== idx);
  });
  const onDropFiles = (e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer?.files);
  };

  useEffect(() => () => { files.forEach((it) => it.preview && URL.revokeObjectURL(it.preview)); }, [files]);

  const uploadDocs = async (jobId) => {
    for (const it of files) {
      const { id: fid, file } = it;
      try {
        setUploadState((s) => ({ ...s, [fid]: { progress: 0, status: "uploading" } }));
        const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });
        await api.post(
          `/deliveries/${jobId}/documents`,
          { filename: file.name, mime_type: file.type || "application/octet-stream", data_base64: b64, kind: "job" },
          { onUploadProgress: (e) => {
              const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
              setUploadState((s) => ({ ...s, [fid]: { progress: pct, status: "uploading" } }));
            } }
        );
        setUploadState((s) => ({ ...s, [fid]: { progress: 100, status: "done" } }));
      } catch {
        setUploadState((s) => ({ ...s, [fid]: { progress: 0, status: "error" } }));
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer_name) return toast.error("Client name is required");
    if (f.dest_lat == null || f.dest_lng == null) return toast.error("Pick an address from suggestions to set the location");
    if (!f.truck_id) return toast.error("Select a truck");
    if (!f.scheduled_at) return toast.error("Set a scheduled time");

    const payload = {
      customer_name: f.customer_name,
      customer_email: f.customer_email || null,
      customer_phone: f.customer_phone || null,
      contact: (f.contact_name || f.contact_phone) ? { name: f.contact_name || null, role: f.contact_role || null, phone: f.contact_phone || null } : null,
      category: f.category,
      job_description: f.job_description || null, po_number: f.po_number || null,
      address: f.address, dest_lat: f.dest_lat, dest_lng: f.dest_lng,
      gated_property: f.gated_property,
      scheduled_at: f.scheduled_at + ":00",
      window_minutes: Number(f.window_minutes) || 60,
      multi_day: f.multi_day, end_at: f.multi_day && f.end_at ? f.end_at + ":00" : null,
      truck_id: f.truck_id, assigned_tech_ids: f.assigned_tech_ids,
      am_name: f.am_name || null, am_email: f.am_email || null, notes_for_techs: f.notes_for_techs || null,
      estimated_duration_min: f.category === "delivery" && f.duration_min ? Number(f.duration_min) : null,
      items: f.items.map((it) => ({ name: it.name.trim(), quantity: it.quantity?.trim() || null, note: it.note?.trim() || null })).filter((it) => it.name),
    };

    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/deliveries/${id}`, payload);
        if (files.length) await uploadDocs(id);
        toast.success("Job updated");
        navigate(`/jobs/${id}`);
      } else {
        const { data } = await api.post("/deliveries", payload);
        if (files.length) await uploadDocs(data.id);
        toast.success("Job created");
        navigate(`/jobs/${data.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-stark-terracotta" /></div>;

  return (
    <div className="mx-auto max-w-4xl">
      <button onClick={() => navigate(-1)} className="mb-5 flex items-center gap-1.5 text-sm font-semibold text-stark-muted hover:text-stark-ink">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="text-3xl font-extrabold tracking-tight text-stark-ink sm:text-4xl">{editing ? "Edit Job" : "New Job"}</h1>
      <p className="mt-1 text-sm text-stark-muted">Schedule an install, delivery, measure, service call or template.</p>

      <form onSubmit={submit} className="mt-6 space-y-5">
        <Section title="Client">
          <div>
            <Label>Client name *</Label>
            <Input data-testid="form-customer-name" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Client email <span className="font-normal text-stark-muted">(optional)</span></Label>
            <Input data-testid="form-customer-email" type="email" value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Client phone</Label>
            <Input value={f.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>On-site contact name</Label>
            <Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Contact role</Label>
            <Input placeholder="Designer, Homeowner…" value={f.contact_role} onChange={(e) => set("contact_role", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Contact phone</Label>
            <Input value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="mt-1.5" />
          </div>
        </Section>

        <Section title="Location">
          <div className="relative sm:col-span-2">
            <Label>Address * <span className="font-normal text-stark-muted">(select a suggestion to set map location)</span></Label>
            <div className="relative mt-1.5">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stark-muted" />
              <Input data-testid="form-address" value={f.address} onChange={(e) => searchAddress(e.target.value)} placeholder="123 Main St, New York, NY" className="pl-9" />
              {geoBusy && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-stark-muted" />}
              {f.dest_lat != null && !geoBusy && <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stark-success" />}
            </div>
            {geoResults.length > 0 && (
              <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-stark-border bg-white shadow-xl">
                {geoResults.map((r, i) => (
                  <button key={i} type="button" onClick={() => pickAddress(r)} className="flex w-full items-start gap-2 border-b border-stark-border px-3.5 py-2.5 text-left text-sm hover:bg-stark-tint last:border-0" data-testid={`geo-result-${i}`}>
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-stark-terracotta" /><span className="text-stark-ink">{r.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between rounded-xl border border-stark-border p-3 sm:col-span-2">
            <div>
              <div className="text-sm font-semibold text-stark-ink">Gated property</div>
              <div className="text-xs text-stark-muted">Crew needs gate access / code.</div>
            </div>
            <Switch checked={f.gated_property} onCheckedChange={(v) => set("gated_property", v)} data-testid="form-gated" />
          </div>
        </Section>

        <Section title="Classification & schedule">
          <div>
            <Label>Category</Label>
            <Select value={f.category} onValueChange={(v) => { set("category", v); set("truck_id", ""); set("assigned_tech_ids", []); }}>
              <SelectTrigger className="mt-1.5" data-testid="form-category"><SelectValue /></SelectTrigger>
              <SelectContent>{FORM_CATEGORIES.map((k) => <SelectItem key={k} value={k}>{CATEGORY_CONFIG[k].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Order # (PO)</Label>
            <Input value={f.po_number} onChange={(e) => set("po_number", e.target.value)} className="mt-1.5" />
          </div>

          {/* Multi-day toggle FIRST, then start/end inline */}
          <div className="flex items-center justify-between rounded-xl border border-stark-border p-3 sm:col-span-2">
            <div className="text-sm font-semibold text-stark-ink">Multi-day job</div>
            <Switch checked={f.multi_day} onCheckedChange={(v) => set("multi_day", v)} data-testid="form-multiday" />
          </div>
          {f.multi_day ? (
            <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
              <div>
                <Label>Start date & time *</Label>
                <div className="mt-1.5"><DateTimePicker testId="form-scheduled" value={f.scheduled_at} onChange={(v) => set("scheduled_at", v)} placeholder="Start" /></div>
              </div>
              <div>
                <Label>End date & time</Label>
                <div className="mt-1.5"><DateTimePicker testId="form-end" value={f.end_at} onChange={(v) => set("end_at", v)} placeholder="End" /></div>
              </div>
            </div>
          ) : (
            <div className="sm:col-span-2">
              <Label>Scheduled at *</Label>
              <div className="mt-1.5"><DateTimePicker testId="form-scheduled" value={f.scheduled_at} onChange={(v) => set("scheduled_at", v)} placeholder="Select date & time" /></div>
            </div>
          )}

          <div>
            <Label>Arrival window (min)</Label>
            <Input type="number" value={f.window_minutes} onChange={(e) => set("window_minutes", e.target.value)} className="mt-1.5" />
          </div>
          {f.category === "delivery" && (
            <div>
              <Label>Est. time on site (min)</Label>
              <Input type="number" min="0" step="15" placeholder="e.g. 45" value={f.duration_min} onChange={(e) => set("duration_min", e.target.value)} className="mt-1.5" data-testid="form-duration" />
              <p className="mt-1 text-xs text-stark-muted">Shows on the delivery calendar to help plan & route.</p>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Job description</Label>
            <Textarea value={f.job_description} onChange={(e) => set("job_description", e.target.value)} rows={2} className="mt-1.5" />
          </div>
        </Section>

        <Section title="Assignment">
          <div>
            <Label>Truck *</Label>
            <Select value={f.truck_id} onValueChange={(v) => set("truck_id", v)}>
              <SelectTrigger className="mt-1.5" data-testid="form-truck"><SelectValue placeholder="Select truck" /></SelectTrigger>
              <SelectContent>{trucks.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.plate}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div />
          <div>
            <Label>Account Manager name</Label>
            <Input list="am-suggestions" data-testid="form-am-name" value={f.am_name} onChange={(e) => onPickAM(e.target.value)} placeholder="Type a name…" className="mt-1.5" />
            <datalist id="am-suggestions">{ams.map((a) => <option key={a.id} value={a.name} />)}</datalist>
          </div>
          <div>
            <Label>Account Manager email</Label>
            <Input data-testid="form-am-email" type="email" value={f.am_email} onChange={(e) => set("am_email", e.target.value)} placeholder="am@company.com" className="mt-1.5" />
          </div>

          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Users className="h-4 w-4" /> Assigned crew ({team})</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-lg" onClick={() => setCrewOpen(true)} data-testid="form-add-crew-btn">
                <UserPlus className="mr-1 h-4 w-4" /> Add crew
              </Button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {techs.length === 0 && <span className="text-sm text-stark-muted">No crew yet — use “Add crew”.</span>}
              {techs.map((t) => {
                const on = f.assigned_tech_ids.includes(t.id);
                return (
                  <button key={t.id} type="button" onClick={() => toggleTech(t.id)} data-testid={`form-tech-${t.username}`}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${on ? "bg-stark-terracotta text-white" : "bg-white text-stark-muted ring-1 ring-stark-border hover:bg-stark-tint"}`}>
                    {on && <Check className="mr-1 inline h-3.5 w-3.5" />}{t.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>Notes for crew</Label>
            <Textarea value={f.notes_for_techs} onChange={(e) => set("notes_for_techs", e.target.value)} rows={2} className="mt-1.5" />
          </div>
        </Section>

        <div className="stark-card p-6">
          <div className="mb-1 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-stark-muted">
              <Package className="h-4 w-4" /> Items to deliver
              {f.items.length > 0 && <span className="rounded-full bg-stark-tint px-2 py-0.5 text-[11px] font-bold text-stark-terracotta">{f.items.length}</span>}
            </h2>
            <Button type="button" size="sm" variant="outline" className="rounded-lg" onClick={addItem} data-testid="form-add-item-btn">
              <Plus className="mr-1 h-4 w-4" /> Add item
            </Button>
          </div>
          <p className="mb-4 text-xs text-stark-muted">List exactly what the crew is delivering so they know what to load & drop at this stop.</p>
          {f.items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-stark-border py-6 text-center text-sm text-stark-muted">
              No items yet — tap “Add item” to build the delivery list.
            </div>
          ) : (
            <div className="space-y-2.5">
              {f.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`form-item-${i}`}>
                  <Input className="col-span-12 sm:col-span-6" placeholder="Item / product" value={it.name} onChange={(e) => setItem(i, "name", e.target.value)} data-testid={`form-item-name-${i}`} />
                  <Input className="col-span-4 sm:col-span-2" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`form-item-qty-${i}`} />
                  <Input className="col-span-7 sm:col-span-3" placeholder="Note (optional)" value={it.note} onChange={(e) => setItem(i, "note", e.target.value)} data-testid={`form-item-note-${i}`} />
                  <button type="button" onClick={() => removeItem(i)} className="col-span-1 flex items-center justify-center text-stark-muted hover:text-stark-error" data-testid={`form-item-remove-${i}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Section title="Documents (optional)">
          <div className="sm:col-span-2">
            <input ref={fileRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="form-doc-input" />
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileRef.current?.click()}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileRef.current?.click(); } }}
              onDragOver={(e) => { e.preventDefault(); if (!dragActive) setDragActive(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={onDropFiles}
              data-testid="form-doc-dropzone"
              className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${dragActive ? "border-stark-terracotta bg-stark-tint" : "border-stark-border bg-stark-surface/50 hover:border-stark-terracotta hover:bg-stark-tint/50"}`}
            >
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${dragActive ? "bg-stark-terracotta text-white" : "bg-stark-tint text-stark-terracotta"}`}>
                <Upload className="h-5 w-5" />
              </div>
              <div className="mt-3 text-sm font-semibold text-stark-ink">
                {dragActive ? "Drop files to attach" : "Drag & drop files here"}
              </div>
              <div className="mt-0.5 text-xs text-stark-muted">
                or <span className="font-semibold text-stark-terracotta">browse</span> · LWO / DWO / layout files · up to 5 MB each
              </div>
            </div>
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((it, i) => {
                  const st = uploadState[it.id] || { progress: 0, status: "pending" };
                  const ext = (it.file.name.split(".").pop() || "").slice(0, 4).toUpperCase();
                  return (
                    <div key={it.id} className="flex items-center gap-3 rounded-xl border border-stark-border p-2.5" data-testid={`form-doc-item-${i}`}>
                      {it.preview ? (
                        <img src={it.preview} alt={it.file.name} className="h-11 w-11 shrink-0 rounded-lg object-cover ring-1 ring-stark-border" />
                      ) : (
                        <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-lg bg-stark-tint text-stark-terracotta">
                          <FileText className="h-4 w-4" />
                          <span className="mt-0.5 text-[8px] font-bold leading-none">{ext}</span>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="flex-1 truncate text-sm font-medium text-stark-ink">{it.file.name}</span>
                          <span className="shrink-0 text-xs text-stark-muted">{(it.file.size / 1024).toFixed(0)} KB</span>
                        </div>
                        {(st.status === "uploading" || st.status === "done") && (
                          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-stark-border" data-testid={`form-doc-progress-${i}`}>
                            <div
                              className={`h-full rounded-full transition-all duration-200 ${st.status === "done" ? "bg-stark-success" : "bg-stark-terracotta"}`}
                              style={{ width: `${st.status === "done" ? 100 : st.progress}%` }}
                            />
                          </div>
                        )}
                        {st.status === "error" && <div className="mt-1 text-[11px] font-semibold text-stark-error">Upload failed</div>}
                      </div>
                      {st.status === "done" ? (
                        <Check className="h-5 w-5 shrink-0 text-stark-success" data-testid={`form-doc-done-${i}`} />
                      ) : st.status === "uploading" ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-stark-terracotta" />
                      ) : (
                        <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="shrink-0 text-stark-muted hover:text-stark-error" data-testid={`form-doc-remove-${i}`}><X className="h-4 w-4" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Section>

        <div className="flex justify-end gap-3 pb-4">
          <Button type="button" variant="outline" className="rounded-xl" onClick={() => navigate(-1)}>Cancel</Button>
          <Button type="submit" disabled={saving} className="rounded-xl bg-stark-terracotta px-8 font-bold hover:bg-[#bd4f2c]" data-testid="form-submit">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : editing ? "Save changes" : "Create job"}
          </Button>
        </div>
      </form>

      <Dialog open={crewOpen} onOpenChange={setCrewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add crew member ({team})</DialogTitle></DialogHeader>
          <p className="text-sm text-stark-muted">Creates a field-crew login. A temporary password is generated — they can update it later in Team.</p>
          <div className="space-y-4">
            <div><Label>Full name</Label><Input value={crew.name} onChange={(e) => setCrew((p) => ({ ...p, name: e.target.value }))} className="mt-1.5" data-testid="crew-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={crew.email} onChange={(e) => setCrew((p) => ({ ...p, email: e.target.value }))} className="mt-1.5" data-testid="crew-email-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrewOpen(false)}>Cancel</Button>
            <Button className="bg-stark-terracotta hover:bg-[#bd4f2c]" disabled={crewBusy} onClick={addCrew} data-testid="crew-save-btn">
              {crewBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add & assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
